import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Shield, Users, FileText, Settings, LogOut, ArrowLeft,
  Search, Download, Eye, Filter, ChevronLeft, ChevronRight,
  Loader2, X, AlertTriangle, Clock, XCircle, Copy, File, Maximize2,
  Lock, Unlock
} from 'lucide-react';
import useAuth from '../hooks/useAuth';
import api from '../utils/api';

// Format duration from milliseconds to human-readable
const formatDuration = (ms) => {
  if (!ms) return '—';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const hours = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (hours > 0) return `${hours}h ${mins}m ${secs}s`;
  return `${mins}m ${secs}s`;
};

// Helper function to get model display name
const getModelDisplayName = (model) => {
  if (!model) return 'Unknown';

  const modelNames = {
    'claude-opus-4-6': 'Opus 4.6',
    'claude-sonnet-4-6': 'Sonnet 4.6',
    'claude-opus-4-5-20251101': 'Opus 4.5',
    'claude-sonnet-4-5-20250929': 'Sonnet 4.5',
    'gpt-5.4': 'GPT-5.4',
    'gpt-5.4-pro': 'GPT-5.4 Pro',
    'gpt-5.2-2025-12-11': 'GPT-5.2',
    'gpt-5-2025-08-07': 'GPT-5',
    'gpt-5-mini': 'GPT-5 Mini',
    'o3-pro-2025-06-10': 'O3 Pro',
    'o3-deep-research-2025-06-26': 'O3 DR',
    'o4-mini-deep-research-2025-06-26': 'O4-Mini DR',
    'o4-mini': 'O4 Mini',
    'gpt-4o': 'GPT-4o',
    'gemini-3.1-pro-preview': 'Gemini 3.1 Pro',
    'gemini-3.1-flash-lite': 'Gemini 3.1 Flash',
    'gemini-2.5-pro-preview-05-06': 'Gemini 2.5 Pro',
    'grok-4': 'Grok 4',
    'grok-4.1-fast': 'Grok 4.1 Fast',
    'moonshotai/kimi-k2.5': 'Kimi 2.5',
    'xai/grok-4': 'Grok 4 (OR)'
  };

  return modelNames[model] || model;
};

// Helper function to get model badge color
const getModelBadgeColor = (model) => {
  if (!model) return 'bg-gray-100 text-gray-700';

  // Anthropic (Claude) - Purple
  if (model.includes('claude')) return 'bg-purple-900/50 text-purple-200 border-purple-700';

  // OpenAI - Green
  if (model.includes('gpt') || model.includes('o1') || model.includes('o3') || model.includes('o4')) {
    return 'bg-green-900/50 text-green-200 border-green-700';
  }

  // Google - Blue
  if (model.includes('gemini')) return 'bg-blue-900/50 text-blue-200 border-blue-700';

  // xAI (Grok) - Orange
  if (model.includes('grok')) return 'bg-orange-900/50 text-orange-200 border-orange-700';

  // OpenRouter (Kimi) - Teal
  if (model.includes('kimi') || model.includes('moonshot')) return 'bg-teal-900/50 text-teal-200 border-teal-700';

  return 'bg-gray-700 text-gray-200 border-gray-600';
};

const AdminReports = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({ type: '', risk: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedReports, setSelectedReports] = useState(new Set());
  const [pollingInterval, setPollingInterval] = useState(null);

  useEffect(() => {
    fetchReports();
    const reportId = searchParams.get('id');
    if (reportId) {
      fetchReportDetails(reportId);
    }
  }, [page, filter, searchParams]);

  const fetchReports = async () => {
    try {
      const params = {
        page,
        limit: 20,
        ...(search && { search }),
        ...(filter.type && { type: filter.type }),
        ...(filter.risk && { risk: filter.risk })
      };
      const response = await api.admin.getReports(params);
      setReports(response.reports);
      setTotalPages(response.totalPages);
    } catch (err) {
      if (err.message?.includes('401') || err.message?.includes('403')) {
        navigate('/admin-portal-7x9k2m');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReportDetails = async (id) => {
    try {
      const response = await api.admin.getReport(id);
      setSelectedReport(response);
    } catch (err) {
      console.error('Failed to fetch report details');
    }
  };

  const handleCancelReport = async (reportId) => {
    if (!window.confirm(`Cancel report ${reportId}? This will stop generation and refund the credit.`)) return;
    try {
      await api.admin.cancelReport(reportId);
      fetchReports();
      if (selectedReport) fetchReportDetails(selectedReport._id);
    } catch (err) {
      alert(err.message || 'Failed to cancel report');
    }
  };

  const handleRetryReport = async (reportId) => {
    if (!window.confirm(`Retry report ${reportId}? This will regenerate the report from scratch.`)) return;
    try {
      await api.admin.retryReport(reportId);
      fetchReports();
      if (selectedReport) fetchReportDetails(selectedReport._id);
    } catch (err) {
      alert(err.message || 'Failed to retry report');
    }
  };

  const handleDownload = async (reportId, format) => {
    setDownloading(reportId);
    try {
      const blob = await api.admin.downloadReport(reportId, format);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report-${reportId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    navigate('/admin-portal-7x9k2m');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchReports();
  };

  // Check if any reports are actively processing
  const hasProcessingReports = () => {
    return reports.some(r => r.status === 'processing' || r.status === 'pending');
  };

  // Auto-refresh effect
  useEffect(() => {
    if (hasProcessingReports() && !pollingInterval) {
      const interval = setInterval(() => {
        fetchReports();
      }, 15000); // 15 seconds
      setPollingInterval(interval);
    }

    if (!hasProcessingReports() && pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [reports, pollingInterval]);

  // Auto-refresh modal detail for processing reports (every 10 seconds)
  useEffect(() => {
    if (!selectedReport || (selectedReport.status !== 'processing' && selectedReport.status !== 'pending')) return;
    const interval = setInterval(() => {
      fetchReportDetails(selectedReport._id);
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedReport?._id, selectedReport?.status]);

  const { user } = useAuth();
  const [selectedAgentOutput, setSelectedAgentOutput] = useState(null);

  const AGENTS = [
    { key: 'webResearch', label: 'Web Research (1)', color: 'blue' },
    { key: 'deepResearch', label: 'Deep Research (2b)', color: 'purple' },
    { key: 'advancedResearch', label: 'Advanced Research (2c)', color: 'indigo' },
    { key: 'supplementaryResearch', label: 'Supplementary Research (2c-sup)', color: 'green' },
    { key: 'secondarySupplementaryResearch', label: 'Secondary Supplementary (2d)', color: 'teal' },
    { key: 'combinedResearch', label: 'Combined Research (to Arranger)', color: 'gray' },
    { key: 'arranger', label: 'Arranger Output (3)', color: 'red' },
    { key: 'checker', label: 'Checker Feedback (4)', color: 'orange' },
    { key: 'orchestrator', label: 'Orchestrator Review (5)', color: 'pink' }
  ];

  const hasPhaseOutputs = selectedReport?.phaseOutputs && Object.keys(selectedReport.phaseOutputs).length > 0;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const downloadAgentOutput = (content, agentName) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-${agentName}-${selectedReport.reportId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Toggle selection for a single report
  const toggleReportSelection = (reportId) => {
    setSelectedReports(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  // Select all completed reports on current page
  const selectAll = () => {
    const completedIds = reports
      .filter(r => r.status === 'completed')
      .map(r => r.reportId);
    setSelectedReports(new Set(completedIds));
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedReports(new Set());
  };

  // Bulk download selected reports
  const handleBulkDownload = async () => {
    if (selectedReports.size === 0) return;

    if (selectedReports.size > 10) {
      alert('Maximum 10 reports per bulk download. Please select fewer reports.');
      return;
    }

    try {
      const reportIds = Array.from(selectedReports);
      const blob = await api.admin.bulkDownload(reportIds);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DD-Reports-${Date.now()}.zip`;
      a.click();
      clearSelection();
    } catch (err) {
      alert('Bulk download failed: ' + err.message);
    }
  };

  // Bulk delete selected reports
  const handleBulkDelete = async () => {
    if (selectedReports.size === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedReports.size} report(s)? This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      const reportIds = Array.from(selectedReports);
      await api.admin.bulkDeleteReports(reportIds);
      clearSelection();
      fetchReports(); // Refresh list
    } catch (err) {
      alert('Bulk delete failed: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-600 rounded-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Portal</h1>
              <p className="text-xs text-gray-400">Due Diligence Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              User Portal
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <Link to="/admin-portal-7x9k2m/dashboard" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Dashboard
            </Link>
            <Link to="/admin-portal-7x9k2m/reports" className="px-4 py-3 text-sm font-medium text-white border-b-2 border-red-500">
              Reports
            </Link>
            <Link to="/admin-portal-7x9k2m/users" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Users
            </Link>
            <Link to="/admin-portal-7x9k2m/config" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Configuration
            </Link>
            <Link to="/admin-portal-7x9k2m/vouchers" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Vouchers
            </Link>
            <Link to="/admin-portal-7x9k2m/reviews" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Reviews
            </Link>
            <Link to="/admin-portal-7x9k2m/audit" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Audit
            </Link>
            <Link to="/admin-portal-7x9k2m/api-keys" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              API Keys
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Search & Filters */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by subject name or user email..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
            <select
              value={filter.type}
              onChange={(e) => setFilter({ ...filter, type: e.target.value })}
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">All Types</option>
              <option value="individual">Individual</option>
              <option value="business">Business</option>
            </select>
            <select
              value={filter.risk}
              onChange={(e) => setFilter({ ...filter, risk: e.target.value })}
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">All Risk Levels</option>
              <option value="low">Low (0-39)</option>
              <option value="medium">Medium (40-69)</option>
              <option value="high">High (70+)</option>
            </select>
            <button
              type="submit"
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
            >
              Search
            </button>
          </form>
        </div>

        {/* Auto-refresh indicator */}
        {hasProcessingReports() && (
          <div className="flex items-center gap-2 text-sm text-gray-300 mb-4 bg-blue-900/30 border border-blue-700 rounded-md p-3">
            <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
            <span>Auto-refreshing every 15 seconds while reports are processing... These advanced reports typically take around 1 hour to prepare.</span>
          </div>
        )}

        {/* Bulk Action Toolbar */}
        {selectedReports.size > 0 && (
          <div className="bg-blue-900/30 border border-blue-700 rounded-md p-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-blue-400" />
              <span className="text-sm font-medium text-blue-200">
                {selectedReports.size} report(s) selected
              </span>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={handleBulkDownload}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
              >
                <Download className="h-4 w-4" />
                Download ZIP
              </button>

              <button
                onClick={handleBulkDelete}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
              >
                <X className="h-4 w-4" />
                Delete
              </button>

              <button
                onClick={clearSelection}
                className="px-4 py-2 border border-gray-600 rounded-md hover:bg-gray-700 text-sm text-gray-300"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Reports Table */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-red-500 animate-spin" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No reports found</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700/50">
                    <tr>
                      <th className="px-3 py-3 w-12">
                        <input
                          type="checkbox"
                          checked={selectedReports.size > 0 && selectedReports.size === reports.filter(r => r.status === 'completed').length}
                          onChange={(e) => e.target.checked ? selectAll() : clearSelection()}
                          className="rounded border-gray-600 text-red-600 focus:ring-red-500 bg-gray-700"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Subject</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Model</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Risk</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Searches</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {reports.map((report) => (
                      <tr key={report._id} className="hover:bg-gray-700/30">
                        <td className="px-3 py-4">
                          {report.status === 'completed' && (
                            <input
                              type="checkbox"
                              checked={selectedReports.has(report.reportId)}
                              onChange={() => toggleReportSelection(report.reportId)}
                              className="rounded border-gray-600 text-red-600 focus:ring-red-500 bg-gray-700"
                            />
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="font-medium text-white">{report.companyName || report.subjectName}</div>
                          <div className="text-xs text-gray-400 font-mono">{report.reportId}</div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300 capitalize">
                            {report.reportType || 'Business'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-mono border ${getModelBadgeColor(report.aiModel || report.selectedModel)}`}>
                            {getModelDisplayName(report.aiModel || report.selectedModel)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            report.riskScore >= 70 ? 'bg-red-900/50 text-red-300' :
                            report.riskScore >= 40 ? 'bg-yellow-900/50 text-yellow-300' :
                            'bg-green-900/50 text-green-300'
                          }`}>
                            {report.riskScore}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-sm font-mono text-gray-300">
                            {report.searchCount || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <span className="text-sm font-mono text-gray-300">
                            {report.reportCost != null && report.reportCost > 0
                              ? `$${(report.reportCost * 1.2).toFixed(4)}`
                              : report.status === 'completed' ? '—' : ''}
                          </span>
                          {report.reportCost > 0 && (
                            <span className="block text-[10px] text-gray-500">inc 20% VAT</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-300">
                          {new Date(report.createdAt).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Content access status indicator */}
                            {report.contentAccessGranted
                              ? <span title={report.isReportOwner ? 'Your report' : 'User has granted content access'} className="text-amber-400"><Unlock className="h-4 w-4" /></span>
                              : <span title="Content access not granted" className="text-gray-600"><Lock className="h-4 w-4" /></span>
                            }
                            <button
                              onClick={() => fetchReportDetails(report._id)}
                              className="p-2 text-gray-400 hover:text-white transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {(report.status === 'processing' || report.status === 'pending') && (
                              <button
                                onClick={() => handleCancelReport(report.reportId)}
                                className="p-2 text-red-400 hover:text-red-300 transition-colors"
                                title="Cancel Report"
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            )}
                            {(() => {
                              const a = report.adminAccess;
                              const canDownload = a?.granted && a?.expiresAt && new Date(a.expiresAt) > new Date();
                              return (
                                <button
                                  onClick={() => canDownload && handleDownload(report._id, 'pdf')}
                                  disabled={downloading === report._id || !canDownload}
                                  className={`p-2 transition-colors ${canDownload ? 'text-gray-400 hover:text-white' : 'text-gray-700 cursor-not-allowed'}`}
                                  title={canDownload ? 'Download PDF' : 'Content access not granted — user must enable sharing'}
                                >
                                  {downloading === report._id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4" />
                                  )}
                                </button>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-400">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded text-white"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded text-white"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Report Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Report Details</h2>
              <button
                onClick={() => setSelectedReport(null)}
                className="p-1 text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">

              {/* Content access status banner */}
              {(() => {
                const a = selectedReport.adminAccess;
                const active = a?.granted && a?.expiresAt && new Date(a.expiresAt) > new Date();
                const expiresAt = active ? new Date(a.expiresAt).toLocaleString('en-GB') : null;
                return active ? (
                  <div className="mb-4 p-3 bg-amber-900/30 border border-amber-600 rounded-md flex items-center gap-2">
                    <Unlock className="h-4 w-4 text-amber-400 flex-shrink-0" />
                    <p className="text-sm text-amber-200">
                      <span className="font-semibold">Content access granted</span> — the user has shared this report. Access expires {expiresAt}.
                    </p>
                  </div>
                ) : (
                  <div className="mb-4 p-3 bg-gray-700/50 border border-gray-600 rounded-md flex items-center gap-2">
                    <Lock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <p className="text-sm text-gray-300">
                      <span className="font-semibold">Report content is private.</span> The user has not granted administrator access. Agent activity and costs are shown below; report text and downloads are unavailable.
                    </p>
                  </div>
                );
              })()}

              <div className="mb-4 px-3 py-2 bg-gray-700 rounded-md flex items-center gap-3">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Report ID</p>
                  <p className="text-sm text-white font-mono">{selectedReport.reportId}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-400">Subject Name</p>
                  <p className="text-white font-medium">{selectedReport.subjectName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Reference</p>
                  <p className="text-white font-medium">{selectedReport.referenceNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">User</p>
                  <p className="text-white font-medium">{selectedReport.userId?.email || selectedReport.user?.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Created</p>
                  <p className="text-white font-medium">
                    {new Date(selectedReport.createdAt).toLocaleString('en-GB')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">AI Cost</p>
                  <p className="text-white font-medium font-mono">
                    {selectedReport.reportCost != null && selectedReport.reportCost > 0
                      ? `$${selectedReport.reportCost.toFixed(4)}`
                      : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Tokens Used</p>
                  <p className="text-white font-medium font-mono">
                    {selectedReport.tokensUsed
                      ? selectedReport.tokensUsed.toLocaleString()
                      : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Generation Time</p>
                  <p className="text-white font-medium font-mono">
                    {formatDuration(selectedReport.processingTime)}
                  </p>
                </div>
              </div>

              {/* Live Status for Processing Reports */}
              {(selectedReport.status === 'processing' || selectedReport.status === 'pending') && (
                <div className="mb-4 p-3 bg-blue-900/30 border border-blue-700 rounded-md flex items-center gap-3">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-blue-200 font-medium">
                      {selectedReport.statusMessage || 'Processing...'}
                    </p>
                    <p className="text-xs text-blue-400 mt-1">
                      Auto-refreshing every 10 seconds
                      {selectedReport.reportCost > 0 && ` | Cost so far: $${selectedReport.reportCost.toFixed(4)}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCancelReport(selectedReport.reportId)}
                    className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded flex items-center gap-1"
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Cancel Report
                  </button>
                </div>
              )}

              {/* Agent Pipeline Details */}
              {selectedReport.agentMetadata && (
                <div className="border-t border-gray-700 pt-4">
                  <h3 className="text-lg font-semibold text-white mb-3">Agent Pipeline Details</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 border-b border-gray-700">
                          <th className="text-left py-2 pr-3">Agent</th>
                          <th className="text-left py-2 pr-3">Model</th>
                          <th className="text-right py-2 pr-3">Input</th>
                          <th className="text-right py-2 pr-3">Output</th>
                          <th className="text-right py-2 pr-3">Duration</th>
                          <th className="text-right py-2">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ['webResearch', 'Web Research (2a)'],
                          ['deepResearch', 'Deep Research (2b)'],
                          ['advancedResearch', 'Advanced Research (2c)'],
                          ['supplementaryResearch', 'Supplementary Research (2c-sup)'],
                          ['secondarySupplementaryResearch', 'Secondary Supplementary (2d)'],
                          ['arranger', 'Arranger (3)'],
                          ['checker', 'Checker (4)'],
                          ['orchestrator', 'Orchestrator (5)']
                        ].map(([key, label]) => {
                          const meta = selectedReport.agentMetadata[key];
                          if (!meta?.model) return null;
                          const fallbackIndex = meta.fallbackIndex ?? 0;
                          const usedFallback = meta.usedFallback === true || fallbackIndex > 0;
                          // Format model display: openrouter/provider/model for OR models
                          const isOR = meta.provider === 'openrouter' || (meta.model && meta.model.includes('/'));
                          const modelDisplay = meta.model === 'failed'
                            ? null
                            : isOR
                              ? `openrouter/${meta.model}`
                              : meta.model;
                          // Get input/output pricing for display
                          const inPrice = meta.inputPrice != null ? Number(meta.inputPrice) : null;
                          const outPrice = meta.outputPrice != null ? Number(meta.outputPrice) : null;
                          return (
                            <React.Fragment key={key}>
                              <tr className="border-b border-gray-700/50">
                                <td className="py-2 pr-3 text-white">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span>{label}</span>
                                    {usedFallback && (
                                      <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold rounded bg-amber-900/60 text-amber-200 border border-amber-700 align-middle" title={`Primary "${meta.configuredModel || 'unknown'}" failed — recovered using fallback #${fallbackIndex}`}>
                                        Fallback #{fallbackIndex}
                                      </span>
                                    )}
                                    {meta.suspectedMinimalOutput && (
                                      <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold rounded bg-red-900/60 text-red-200 border border-red-700 align-middle" title={`Output suspiciously short (${meta.outputTokens || 0} tokens, ${meta.outputChars || 0} chars) — model may not have executed research properly`}>
                                        Minimal output
                                      </span>
                                    )}
                                  </div>
                                  {selectedReport.phaseOutputs?.[key] && (user?.role === 'admin' || user?.role === 'superadmin') && (
                                    <button
                                      onClick={() => setSelectedAgentOutput({ key, label, content: selectedReport.phaseOutputs[key] })}
                                      className="mt-0.5 flex items-center gap-1 text-[11px] text-blue-400 hover:text-blue-300 transition-colors"
                                      title={`View ${label} raw output`}
                                    >
                                      <Eye className="h-3 w-3" />
                                      View output
                                    </button>
                                  )}
                                </td>
                                <td className="py-2 pr-3 text-gray-300 font-mono text-xs">
                                  {meta.model === 'failed'
                                    ? <span className="text-red-400">FAILED</span>
                                    : <span>{modelDisplay}</span>}
                                  {meta.zdr === true && (
                                    <span className="ml-1 inline-block px-1 py-0.5 text-[9px] font-semibold rounded bg-purple-900/60 text-purple-200 border border-purple-700 align-middle" title="Zero Data Retention">ZDR</span>
                                  )}
                                  {inPrice != null && outPrice != null && (
                                    <span className="ml-1 text-[10px] text-gray-500">
                                      (${inPrice.toFixed(2)}/${outPrice.toFixed(2)})
                                    </span>
                                  )}
                                  {usedFallback && meta.configuredModel && meta.configuredModel !== meta.model && (
                                    <div className="text-[10px] text-amber-400 mt-0.5">
                                      configured: <span className="line-through">{meta.configuredModel}</span>
                                    </div>
                                  )}
                                </td>
                                <td className="py-2 pr-3 text-right text-gray-300 font-mono">{(meta.inputTokens || 0).toLocaleString()}</td>
                                <td className="py-2 pr-3 text-right text-gray-300 font-mono">{(meta.outputTokens || 0).toLocaleString()}</td>
                                <td className="py-2 pr-3 text-right text-gray-300 font-mono text-xs">
                                  {formatDuration(meta.duration)}
                                </td>
                                <td className="py-2 text-right text-white font-mono">
                                  {selectedReport.costBreakdown?.[key] != null
                                    ? `$${selectedReport.costBreakdown[key].toFixed(4)}`
                                    : '—'}
                                </td>
                              </tr>
                              {meta.failedAttempts?.length > 0 && (
                                <tr className="border-b border-gray-700/50">
                                  <td colSpan="6" className="py-1 px-4">
                                    <div className="text-xs text-orange-400 bg-orange-900/20 rounded px-2 py-1 space-y-0.5">
                                      {meta.failedAttempts.map((fa, i) => (
                                        <div key={i}>
                                          <span className="font-semibold">↳ attempt #{fa.fallbackIndex ?? i}:</span>{' '}
                                          <span className="font-mono">{fa.provider}/{fa.model}</span>
                                          {fa.error && <span className="ml-1 italic opacity-80">— {fa.error.length > 150 ? fa.error.slice(0, 150) + '…' : fa.error}</span>}
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        {(() => {
                          const agents = [
                            selectedReport.agentMetadata.webResearch,
                            selectedReport.agentMetadata.deepResearch,
                            selectedReport.agentMetadata.advancedResearch,
                            selectedReport.agentMetadata.supplementaryResearch,
                            selectedReport.agentMetadata.secondarySupplementaryResearch,
                            selectedReport.agentMetadata.arranger,
                            selectedReport.agentMetadata.checker,
                            selectedReport.agentMetadata.orchestrator
                          ];
                          const totalIn = agents.reduce((sum, m) => sum + (m?.inputTokens || 0), 0);
                          const totalOut = agents.reduce((sum, m) => sum + (m?.outputTokens || 0), 0);
                          const totalMs = agents.reduce((sum, m) => sum + (m?.duration || 0), 0);
                          // ── VAT + GBP ─────────────────────────────────
                          const summary = selectedReport.costSummary || {};
                          const vatRate = typeof summary.vatRate === 'number' ? summary.vatRate : (typeof selectedReport.vatRate === 'number' ? selectedReport.vatRate : 0.20);
                          const usdExVat = summary.usdExVat ?? (selectedReport.reportCost || 0);
                          const usdIncVat = summary.usdIncVat ?? (usdExVat * (1 + vatRate));
                          const gbpRate = summary.gbpUsdRate ?? (selectedReport.gbpUsdRate ?? null);
                          const gbpRateDate = summary.gbpUsdRateDate ?? (selectedReport.gbpUsdRateDate ?? null);
                          const gbpIncVat = summary.gbpIncVat ?? (gbpRate ? (usdIncVat / gbpRate) : null);
                          return (
                            <>
                              <tr className="border-t border-gray-600 font-semibold">
                                <td colSpan="2" className="py-2 text-white">Total (USD, ex VAT)</td>
                                <td className="py-2 pr-3 text-right text-white font-mono">{totalIn.toLocaleString()}</td>
                                <td className="py-2 pr-3 text-right text-white font-mono">{totalOut.toLocaleString()}</td>
                                <td className="py-2 pr-3 text-right text-white font-mono text-xs">{formatDuration(totalMs)}</td>
                                <td className="py-2 text-right text-white font-mono">${usdExVat.toFixed(4)}</td>
                              </tr>
                              <tr>
                                <td colSpan="5" className="py-1 text-xs text-gray-400">+ UK VAT @ {(vatRate * 100).toFixed(0)}%</td>
                                <td className="py-1 text-right text-gray-400 font-mono text-xs">${(usdIncVat - usdExVat).toFixed(4)}</td>
                              </tr>
                              <tr>
                                <td colSpan="5" className="py-1 text-sm text-white font-medium">Total (USD, inc VAT)</td>
                                <td className="py-1 text-right text-white font-mono">${usdIncVat.toFixed(4)}</td>
                              </tr>
                              <tr className="border-t border-gray-600">
                                <td colSpan="5" className="py-2 text-white font-semibold">
                                  Total (GBP, inc VAT)
                                  {gbpRate && gbpRateDate ? (
                                    <span className="ml-2 text-[10px] text-gray-400 font-normal">
                                      (mid-rate £1 = ${gbpRate.toFixed(4)} on {gbpRateDate})
                                    </span>
                                  ) : (
                                    <span className="ml-2 text-[10px] text-amber-400 font-normal">
                                      (GBP rate unavailable)
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 text-right text-red-400 font-mono font-bold">
                                  {gbpIncVat !== null ? `£${gbpIncVat.toFixed(4)}` : '—'}
                                </td>
                              </tr>
                            </>
                          );
                        })()}
                      </tfoot>
                    </table>
                  </div>
                  {/* Cost by Provider summary — pivot of agentMetadata costs grouped by provider */}
                  {(() => {
                    const AGENT_KEYS = ['webResearch','deepResearch','advancedResearch','supplementaryResearch','secondarySupplementaryResearch','arranger','checker','orchestrator'];
                    const inferProvider = (meta) => {
                      if (!meta) return 'other';
                      if (meta.provider) return meta.provider;
                      const m = (meta.model || '').toLowerCase();
                      if (m.startsWith('claude-')) return 'anthropic';
                      if (m.startsWith('gpt-') || m.startsWith('o3') || m.startsWith('o4-')) return 'openai';
                      if (m.startsWith('gemini-')) return 'google';
                      if (m.startsWith('grok-')) return 'xai';
                      if (m.includes('/')) return 'openrouter';
                      return 'other';
                    };
                    const labels = { anthropic: 'Anthropic', openai: 'OpenAI', google: 'Google', xai: 'xAI', openrouter: 'OpenRouter', other: 'Other' };
                    const totals = {};
                    AGENT_KEYS.forEach(k => {
                      const meta = selectedReport.agentMetadata?.[k];
                      if (!meta || meta.model === 'failed') return;
                      const cost = selectedReport.costBreakdown?.[k];
                      if (cost == null) return;
                      const p = inferProvider(meta);
                      totals[p] = (totals[p] || 0) + cost;
                    });
                    const entries = Object.entries(totals).sort((a,b) => b[1] - a[1]);
                    if (!entries.length) return null;
                    return (
                      <div className="mt-3 pt-3 border-t border-gray-700/50">
                        <div className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-2">Cost by Provider</div>
                        <div className="flex flex-wrap gap-2">
                          {entries.map(([provider, total]) => (
                            <div key={provider} className="px-3 py-1.5 bg-gray-700/50 border border-gray-600 rounded text-xs flex items-center gap-2">
                              <span className="text-gray-300 font-medium">{labels[provider] || provider}</span>
                              <span className="font-mono font-semibold text-green-400">${total.toFixed(4)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  {/* Combined Research — no agentMetadata row, so shown separately */}
                  {selectedReport.phaseOutputs?.combinedResearch && (user?.role === 'admin' || user?.role === 'superadmin') && (
                    <div className="mt-3 pt-3 border-t border-gray-700/50">
                      <button
                        onClick={() => setSelectedAgentOutput({ key: 'combinedResearch', label: 'Combined Research (input to Arranger)', content: selectedReport.phaseOutputs.combinedResearch })}
                        className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        View Combined Research — full merged input passed to the Arranger
                        <span className="text-xs text-gray-500 font-mono">
                          ({typeof selectedReport.phaseOutputs.combinedResearch === 'string'
                            ? `${selectedReport.phaseOutputs.combinedResearch.length.toLocaleString()} chars`
                            : 'object'})
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {selectedReport.status === 'failed' && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-md flex items-center gap-3">
                  <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-red-200 font-medium">
                      {selectedReport.errorMessage || 'Report generation failed'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRetryReport(selectedReport.reportId)}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded flex items-center gap-1"
                  >
                    Retry Report
                  </button>
                </div>
              )}


              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-lg font-semibold text-white mb-3">Executive Summary</h3>
                <p className="text-gray-300 whitespace-pre-wrap">{selectedReport.content?.executiveSummary}</p>
              </div>

              {selectedReport.content?.keyFindings && (
                <div className="border-t border-gray-700 pt-4 mt-4">
                  <h3 className="text-lg font-semibold text-white mb-3">Key Findings</h3>
                  <ul className="space-y-2">
                    {selectedReport.content.keyFindings.map((finding, idx) => (
                      <li key={idx} className="text-gray-300 flex items-start gap-2">
                        <span className="text-red-400 mt-1">•</span>
                        {finding}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => handleDownload(selectedReport._id, 'docx')}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Word
              </button>
              <button
                onClick={() => handleDownload(selectedReport._id, 'pdf')}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                PDF
              </button>
            </div>
          </div>

          {/* Agent Output Pop-out Modal */}
          {selectedAgentOutput && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setSelectedAgentOutput(null)}>
              <div className="bg-gray-800 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-gray-600" onClick={e => e.stopPropagation()}>
                <div className="px-6 py-4 bg-gray-900/50 border-b border-gray-600 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg">
                      <File className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{selectedAgentOutput.label}</h3>
                      <p className="text-sm text-gray-400">{selectedReport.reportId}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        copyToClipboard(selectedAgentOutput.content);
                      }}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      title="Copy to clipboard"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => downloadAgentOutput(selectedAgentOutput.content, selectedAgentOutput.key)}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      title="Download TXT"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setSelectedAgentOutput(null)}
                      className="p-2 hover:bg-red-700/50 rounded-lg transition-colors text-gray-400 hover:text-white"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="p-6 overflow-y-auto max-h-[70vh] bg-gray-900/50">
                  <pre className="whitespace-pre-wrap text-sm leading-relaxed text-gray-200 font-mono bg-black/20 p-4 rounded-lg border border-gray-700">
                    {selectedAgentOutput.content}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminReports;
